/**
 * About Sheet Component
 *
 * View profile modal: team about text (from ProductTeamDetails), then for each
 * selected product — disclaimer, prescribing link, and Indication and Usage (heading + PI copy).
 */

import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { X } from 'lucide-react-native';
import { Linking } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';

import { DisclaimerText } from '@/components/MedicalOnDemand/DisclaimerText';
import { ProductDetailTab } from '@/components/MedicalOnDemand/ProductDetailTab';
import { ProductTeamDetails } from '@/components/MedicalOnDemand/ProductTeamDetails';
import { MobileSheetHeader } from '@/components/MedicalOnDemand/mobile/MobileSheetHeader';
import {
  Actionsheet,
  ActionsheetBackdrop,
  ActionsheetContent,
  ActionsheetDragIndicator,
  ActionsheetDragIndicatorWrapper,
  ActionsheetScrollView,
} from '@/components/ui/actionsheet';
import { Button, ButtonText } from '@/components/ui/button';
import { Divider } from '@/components/ui/divider';
import { Heading } from '@/components/ui/heading';
import { Icon } from '@/components/ui/icon';
import { LinkText } from '@/components/ui/link';
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
} from '@/components/ui/modal';
import { Text } from '@/components/ui/text';
import { useBreakpointValue } from '@/components/ui/utils/use-break-point-value';
import { VStack } from '@/components/ui/vstack';
import {
  getProductData,
  PharmaProductSupportTeam,
  Product,
  ProductId,
} from '@/constants/medicalOnDemand';
import { useModTeamName } from '@/hooks/useModTeamName';

type ProductTeamDetailsSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  pharmaProductSupportTeam: PharmaProductSupportTeam;
  selectedProduct?: Product | null;
  selectedProductIds?: ProductId[];
};

export function ProductTeamDetailsSheet({
  isOpen,
  onClose,
  pharmaProductSupportTeam,
  selectedProduct,
  selectedProductIds,
}: ProductTeamDetailsSheetProps) {
  const insets = useSafeAreaInsets();

  const teamName = useModTeamName(pharmaProductSupportTeam.name);

  const selectedProducts = (selectedProductIds ?? [])
    .map((id) => getProductData(id))
    .filter((product): product is Product => !!product);

  const hasStructuredProducts = selectedProducts.length > 0;

  const size =
    useBreakpointValue({
      default: 'lg',
      md: 'md',
    }) || 'md';

  const content = (
    <>
      <VStack className="gap-6">
        <ProductTeamDetails
          pharmaProductSupportTeam={pharmaProductSupportTeam}
          selectedProduct={hasStructuredProducts ? null : selectedProduct}
          hideHeader
        />

        {hasStructuredProducts ? (
          <>
            <VStack className="gap-6">
              <Text className="font-bold text-base text-primary-500">
                {`${selectedProducts.some((product) => product.indicationLabel === 'Indications') ? 'Indications' : 'Indication'} and Usage`}
              </Text>
              {selectedProducts.map((product) => (
                <VStack key={product.id} className="gap-3">
                  <ProductDetailTab
                    product={product}
                    variant="stacked"
                    showStackedHeading={false}
                  />
                  <DisclaimerText>{product.disclaimer}</DisclaimerText>
                  <LinkText
                    size="xs"
                    className="font-medium text-primary-500 underline"
                    onPress={() => {
                      Linking.openURL(product.prescribingInfoUrl).catch(() => {});
                    }}>
                    View full Prescribing Information
                  </LinkText>
                </VStack>
              ))}
            </VStack>
          </>
        ) : null}
      </VStack>
    </>
  );

  if (!isWeb) {
    return (
      <Actionsheet isOpen={isOpen} onClose={onClose}>
        <ActionsheetBackdrop />
        <ActionsheetContent
          className="w-full rounded-t-2xl p-0"
          style={{ maxHeight: '95%', minHeight: '85%', paddingBottom: insets.bottom }}>
          <ActionsheetDragIndicatorWrapper>
            <ActionsheetDragIndicator />
          </ActionsheetDragIndicatorWrapper>

          <VStack className="w-full flex-1">
            <VStack className="px-6 pb-4">
              <MobileSheetHeader
                title={`About ${teamName}:`}
                onClose={onClose}
                titleSize="md"
                titleClassName="text-typography-600"
                closeIconSize="md"
              />
            </VStack>

            <Divider className="flex-shrink-0" />

            <ActionsheetScrollView className="min-h-0 flex-1" contentContainerClassName="px-6 py-6">
              {content}
            </ActionsheetScrollView>
          </VStack>
        </ActionsheetContent>
      </Actionsheet>
    );
  }

  return (
    <Modal isOpen={isOpen} size={size} onClose={onClose}>
      <ModalBackdrop />

      <ModalContent
        className={twJoin(
          'min-h-0 flex-col overflow-hidden',
          isWeb ? 'max-h-[85vh]' : 'max-h-[95vh] min-h-[85vh]'
        )}>
        <ModalHeader className="flex-shrink-0 pb-6">
          <Heading size="md" className="text-typography-600">
            About {teamName}:
          </Heading>
          <ModalCloseButton className="mb-6">
            <Icon as={X} size="md" className="text-typography-500" />
          </ModalCloseButton>
        </ModalHeader>

        <Divider className="flex-shrink-0" />

        <ModalBody
          className="min-h-0 flex-1 py-6"
          contentContainerStyle={{ paddingBottom: insets.bottom }}
          showsVerticalScrollIndicator>
          {content}

          <Button onPress={onClose} className="mt-6 w-full md:w-auto">
            <ButtonText>Close</ButtonText>
          </Button>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
